import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import * as React from "react";
import { Component, useEffect, useMemo, useRef, useState, type ReactNode, type RefObject } from "react";
import { DollarSign, Eye, MousePointerClick, UserPlus, Target, TrendingUp, Activity, Wifi, WifiOff, CalendarIcon, X, Globe, Sparkles, Link2, Link2Off, ImageDown, Share2, LayoutDashboard, Bot, CheckCircle2, MessageSquare, Clock as ClockIcon, ArrowRight } from "lucide-react";
import { toPng, toSvg } from "html-to-image";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import type { DateRange } from "react-day-picker";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { ChartContainer, ChartTooltip, type ChartConfig } from "@/components/ui/chart";
import { Area, AreaChart, CartesianGrid, Line, LineChart, XAxis, YAxis } from "recharts";
import { zodValidator, fallback } from "@tanstack/zod-adapter";
import { z } from "zod";
import { format } from "date-fns";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { useTrialLimits } from "@/hooks/useTrialLimits";
import { CampaignHealthWidget } from "@/components/CampaignHealthWidget";
import { PlatformBreakdown } from "@/components/PlatformBreakdown";
import { AudienceInsights } from "@/components/AudienceInsights";
import { MetricDetailDialog, type MetricKey } from "@/components/MetricDetailDialog";
import { AnalyticsInsights } from "@/components/AnalyticsInsights";
import { Skeleton } from "@/components/ui/skeleton";
import { Download } from "lucide-react";
import { getPlatformMetrics, type PlatformId } from "@/lib/platform-mock-data";
import { useI18n } from "@/lib/i18n";
import { Clock } from "lucide-react";
import { requestShareLink } from "@/utils/share-link.functions";

class DashboardSectionBoundary extends Component<
  { children: ReactNode; fallback?: ReactNode; label: string },
  { hasError: boolean }
> {
  constructor(props: { children: ReactNode; fallback?: ReactNode; label: string }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: unknown) {
    console.error(`Dashboard section failed: ${this.props.label}`, error);
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback ?? null;
    }
    return this.props.children;
  }
}

const PRESET_RANGES = [
  { id: "7", labelKey: "dash.range.7", days: 7 },
  { id: "30", labelKey: "dash.range.30", days: 30 },
  { id: "90", labelKey: "dash.range.90", days: 90 },
] as const;
type PresetId = (typeof PRESET_RANGES)[number]["id"] | "custom" | "all";

const PLATFORM_OPTIONS: { id: PlatformId | "all"; labelKey: string; staticLabel?: string }[] = [
  { id: "all", labelKey: "dash.platform.all" },
  { id: "meta", labelKey: "", staticLabel: "Meta" },
  { id: "tiktok", labelKey: "", staticLabel: "TikTok" },
  { id: "google", labelKey: "", staticLabel: "Google" },
  { id: "youtube", labelKey: "", staticLabel: "YouTube" },
];

// Map platform filter to the campaign.platform values stored in DB.
// Accept common synonyms (e.g. "facebook" for Meta).
function campaignMatchesPlatform(platform: string | undefined | null, filter: PlatformId | "all") {
  if (filter === "all") return true;
  const p = (platform ?? "").toLowerCase();
  if (filter === "meta") return p === "meta" || p === "facebook" || p === "instagram";
  if (filter === "youtube") return p === "youtube" || p === "google_youtube";
  return p === filter;
}

type Metric = {
  id: string;
  campaign_id: string;
  user_id: string;
  recorded_at: string;
  spend: number;
  reach: number;
  clicks: number;
  leads: number;
  conversions: number;
  revenue: number;
};

type Campaign = { id: string; name: string; platform: string; status: string };

const CURRENCIES = ["USD", "EUR", "GBP", "CAD", "AUD", "JPY", "INR", "BRL", "MXN", "CHF"] as const;
type Currency = (typeof CURRENCIES)[number];

// Region → supported currency. EU countries map to EUR; everything unknown falls back to USD.
const REGION_CURRENCY: Record<string, Currency> = {
  US: "USD", PR: "USD", EC: "USD", SV: "USD",
  GB: "GBP",
  CA: "CAD",
  AU: "AUD", NZ: "AUD",
  JP: "JPY",
  IN: "INR",
  BR: "BRL",
  MX: "MXN",
  CH: "CHF", LI: "CHF",
  // Eurozone
  AT: "EUR", BE: "EUR", CY: "EUR", DE: "EUR", EE: "EUR", ES: "EUR", FI: "EUR",
  FR: "EUR", GR: "EUR", HR: "EUR", IE: "EUR", IT: "EUR", LT: "EUR", LU: "EUR",
  LV: "EUR", MT: "EUR", NL: "EUR", PT: "EUR", SI: "EUR", SK: "EUR",
};

function detectCurrencyFromLocale(): Currency {
  if (typeof navigator === "undefined") return "USD";
  const candidates = [navigator.language, ...(navigator.languages ?? [])].filter(Boolean);
  for (const tag of candidates) {
    try {
      const region = new Intl.Locale(tag).maximize().region;
      if (region && REGION_CURRENCY[region]) return REGION_CURRENCY[region];
    } catch {
      // ignore malformed tags
    }
  }
  return "USD";
}

const dashboardSearchSchema = z.object({
  from: fallback(z.string().optional(), undefined).default(undefined),
  to: fallback(z.string().optional(), undefined).default(undefined),
  campaign: fallback(z.string().optional(), undefined).default(undefined),
  currency: fallback(z.enum(CURRENCIES).optional(), undefined).default(undefined),
  range: fallback(z.enum(["7", "30", "90", "custom", "all"]).optional(), undefined).default(undefined),
  platform: fallback(z.enum(["all", "meta", "tiktok", "google", "youtube"]).optional(), undefined).default(undefined),
  // Unix seconds. Must be a positive finite int within the next ~10 years to be considered valid.
  expires: fallback(z.coerce.number().int().positive().max(4102444800).optional(), undefined).default(undefined),
});

export const Route = createFileRoute("/_app/dashboard")({
  component: DashboardPage,
  validateSearch: zodValidator(dashboardSearchSchema),
  head: () => ({
    meta: [
      { title: "Dashboard · AdKavi" },
      { name: "description", content: "Real-time view of your ad spend, reach, leads, and ROAS." },
    ],
  }),
});

function DashboardPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const search = Route.useSearch();
  const { user } = useAuth();
  const { activeClientId, activeClient } = useTrialLimits();
  const [metrics, setMetrics] = useState<Metric[]>([]);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [metricsLoading, setMetricsLoading] = useState(true);
  const [live, setLive] = useState(false);
  const [savedCurrency, setSavedCurrency] = useState<Currency | null>(null);
  const [metaAccount, setMetaAccount] = useState<string | null>(null);
  const [metaLoaded, setMetaLoaded] = useState(false);
  const [drillMetric, setDrillMetric] = useState<MetricKey | null>(null);
  const [drillLabel, setDrillLabel] = useState("");
  const [drillValue, setDrillValue] = useState("");
  const [hasAnyConnection, setHasAnyConnection] = useState<boolean | null>(null);

  const platformFilter: PlatformId | "all" = (search.platform as PlatformId | "all" | undefined) ?? "all";
  const rangePreset: PresetId = (search.range as PresetId | undefined) ?? (search.from || search.to ? "custom" : "30");

  // Honor 7-day expiry on shared dashboard links
  const shareExpired = typeof search.expires === "number" && search.expires * 1000 < Date.now();

  // Persist filters to localStorage and restore on first mount
  const FILTERS_KEY = "adkavi:dashboard:filters";
  const restoredRef = useRef(false);
  useEffect(() => {
    if (restoredRef.current) return;
    restoredRef.current = true;
    if (search.range || search.platform || search.from || search.to) return;
    try {
      const raw = localStorage.getItem(FILTERS_KEY);
      if (!raw) return;
      const saved = JSON.parse(raw) as { range?: PresetId; platform?: PlatformId | "all"; from?: string; to?: string };
      if (saved && (saved.range || saved.platform || saved.from || saved.to)) {
        navigate({
          to: "/dashboard",
          search: (prev: Record<string, unknown>) => ({ ...prev, ...saved }),
          replace: true,
        });
      }
    } catch {
      // ignore
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(
        FILTERS_KEY,
        JSON.stringify({
          range: search.range,
          platform: search.platform,
          from: search.from,
          to: search.to,
        }),
      );
    } catch {
      // ignore
    }
  }, [search.range, search.platform, search.from, search.to]);

  // Effective date window: explicit URL dates win; otherwise derive from preset.
  const { fromDate, toDate } = useMemo(() => {
    if (search.from || search.to) {
      return {
        fromDate: search.from ? new Date(search.from) : undefined,
        toDate: search.to ? new Date(search.to) : undefined,
      };
    }
    if (rangePreset === "all" || rangePreset === "custom") {
      return { fromDate: undefined, toDate: undefined };
    }
    const days = Number(rangePreset);
    const to = new Date();
    const from = new Date();
    from.setDate(from.getDate() - (days - 1));
    from.setHours(0, 0, 0, 0);
    return { fromDate: from, toDate: to };
  }, [search.from, search.to, rangePreset]);

  const campaignId = search.campaign;
  // URL takes priority; otherwise fall back to the profile-saved choice; finally USD
  const currency: Currency = search.currency ?? savedCurrency ?? "USD";

  const formatCurrency = useMemo(() => {
    const fmt = new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
      maximumFractionDigits: 2,
    });
    return (n: number) => fmt.format(n);
  }, [currency]);

  // Load saved currency preference from profile; if none, infer from browser locale
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    supabase
      .from("profiles")
      .select("preferred_currency")
      .eq("user_id", user.id)
      .maybeSingle()
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error) {
          console.error(error);
          return;
        }
        const c = data?.preferred_currency as string | null | undefined;
        if (c && (CURRENCIES as readonly string[]).includes(c)) {
          setSavedCurrency(c as Currency);
          return;
        }
        // No saved preference — auto-detect from browser locale and persist.
        const detected = detectCurrencyFromLocale();
        setSavedCurrency(detected);
        supabase
          .from("profiles")
          .update({ preferred_currency: detected })
          .eq("user_id", user.id)
          .then(({ error: updateError }) => {
            if (updateError) console.error("Failed to save detected currency", updateError);
          });
      });
    return () => { cancelled = true; };
  }, [user]);

  // Load campaigns for filter dropdown (scoped to active client)
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    let q = supabase
      .from("campaigns")
      .select("id, name, platform, status")
      .eq("user_id", user.id)
      .order("name", { ascending: true });
    if (activeClientId) q = q.eq("client_id", activeClientId);
    q.then(({ data, error }) => {
      if (cancelled) return;
      if (error) console.error(error);
      setCampaigns((data as Campaign[]) ?? []);
    });
    return () => { cancelled = true; };
  }, [user, activeClientId]);

  // Load Meta/Facebook connection status
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    supabase
      .from("meta_connections")
      .select("account_name")
      .eq("user_id", user.id)
      .maybeSingle()
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error) console.error(error);
        setMetaAccount((data?.account_name as string | undefined) ?? null);
        setMetaLoaded(true);
      });
    return () => { cancelled = true; };
  }, [user]);

  // Detect whether ANY ad account (Meta / Google / TikTok) is connected.
  // While unknown (null) we keep the dashboard showing mock data so it's
  // never blank for a brand new user.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    Promise.all([
      supabase.from("meta_connections").select("user_id").eq("user_id", user.id).limit(1),
      supabase.from("google_connections").select("user_id").eq("user_id", user.id).limit(1),
      supabase.from("tiktok_connections").select("user_id").eq("user_id", user.id).limit(1),
    ]).then(([m, g, tk]) => {
      if (cancelled) return;
      const any = Boolean(
        (m.data && m.data.length > 0) ||
        (g.data && g.data.length > 0) ||
        (tk.data && tk.data.length > 0),
      );
      setHasAnyConnection(any);
    });
    return () => { cancelled = true; };
  }, [user]);

  const filteredMetrics = useMemo(() => {
    const allowedByClient = activeClientId ? new Set(campaigns.map((c) => c.id)) : null;
    const platformAllowed = new Set(
      campaigns.filter((c) => campaignMatchesPlatform(c.platform, platformFilter)).map((c) => c.id),
    );
    return metrics.filter((m) => {
      if (allowedByClient && !allowedByClient.has(m.campaign_id)) return false;
      if (platformFilter !== "all" && !platformAllowed.has(m.campaign_id)) return false;
      if (campaignId && m.campaign_id !== campaignId) return false;
      const t = new Date(m.recorded_at).getTime();
      if (fromDate && t < fromDate.getTime()) return false;
      if (toDate) {
        // Make 'to' inclusive of the whole day
        const endOfDay = new Date(toDate);
        endOfDay.setHours(23, 59, 59, 999);
        if (t > endOfDay.getTime()) return false;
      }
      return true;
    });
  }, [metrics, campaigns, activeClientId, campaignId, fromDate, toDate, platformFilter]);

  // Show mock per-platform sample data whenever the user has no live ad-account
  // connection (Meta / Google / TikTok), or has no metrics in the current
  // window. This keeps the dashboard populated with realistic numbers per
  // platform (Meta, TikTok, Google, YouTube) until real data flows in.
  const { displayMetrics, usingMockData } = useMemo(() => {
    // While we still don't know whether anything is connected, prefer mock
    // data over a flash of an empty dashboard for new users.
    const noConnections = hasAnyConnection === false || hasAnyConnection === null;
    const noLiveMetrics = !metricsLoading && filteredMetrics.length === 0;

    if (!noConnections && filteredMetrics.length > 0) {
      return { displayMetrics: filteredMetrics, usingMockData: false };
    }
    if (metricsLoading && hasAnyConnection !== false) {
      // Real account connected and metrics still loading → show skeletons,
      // not mock data.
      return { displayMetrics: filteredMetrics, usingMockData: false };
    }
    if (!noConnections && !noLiveMetrics) {
      return { displayMetrics: filteredMetrics, usingMockData: false };
    }

    const platformRows = getPlatformMetrics().filter((p) =>
      platformFilter === "all" ? true : p.id === platformFilter,
    );

    // Spread the per-platform totals across N days so charts have a curve.
    const days = 30;
    const now = Date.now();
    const synthesized: Metric[] = [];
    platformRows.forEach((p) => {
      const dailyImpr = p.impressions / days;
      const dailyClicks = p.clicks / days;
      const dailySpend = p.spend / days;
      const dailyConv = p.conversions / days;
      const dailyRev = p.revenue / days;
      for (let i = 0; i < days; i++) {
        // Mild deterministic variance so the chart isn't a flat line.
        const wave = 1 + Math.sin((i / days) * Math.PI * 2) * 0.18 + ((i * 7) % 5) * 0.02;
        const date = new Date(now - (days - 1 - i) * 86_400_000);
        synthesized.push({
          id: `mock-${p.id}-${i}`,
          campaign_id: `mock-${p.id}`,
          user_id: user?.id ?? "mock",
          recorded_at: date.toISOString(),
          spend: +(dailySpend * wave).toFixed(2),
          reach: Math.round(dailyImpr * wave),
          clicks: Math.round(dailyClicks * wave),
          leads: Math.round(dailyConv * 0.6 * wave),
          conversions: Math.round(dailyConv * wave),
          revenue: +(dailyRev * wave).toFixed(2),
        } as Metric);
      }
    });
    return { displayMetrics: synthesized, usingMockData: true };
  }, [filteredMetrics, metricsLoading, platformFilter, user?.id, hasAnyConnection]);


  type DashSearch = {
    from?: string;
    to?: string;
    campaign?: string;
    currency?: Currency;
    range?: PresetId;
    platform?: PlatformId | "all";
  };
  const updateSearch = (next: Partial<DashSearch>) => {
    navigate({
      to: "/dashboard",
      search: (prev: DashSearch) => ({ ...prev, ...next }),
    });
  };

  const setRangePreset = (id: PresetId) => {
    if (id === "custom") {
      updateSearch({ range: "custom" });
      return;
    }
    // Clear explicit dates so the preset takes effect.
    updateSearch({ range: id, from: undefined, to: undefined });
  };

  const setCurrency = (next: Currency) => {
    updateSearch({ currency: next });
    setSavedCurrency(next);
    if (user) {
      supabase
        .from("profiles")
        .update({ preferred_currency: next })
        .eq("user_id", user.id)
        .then(({ error }) => {
          if (error) console.error("Failed to save currency", error);
        });
    }
  };

  const clearFilters = () => {
    // Preserve currency selection when clearing filters
    navigate({ to: "/dashboard", search: { currency } });
  };

  const hasFilters = Boolean(
    search.from || search.to || search.campaign || search.range || (search.platform && search.platform !== "all"),
  );

  // Initial load + realtime subscription
  useEffect(() => {
    if (!user) return;

    let cancelled = false;

    supabase
      .from("campaign_metrics")
      .select("*")
      .eq("user_id", user.id)
      .order("recorded_at", { ascending: false })
      .limit(1000)
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error) console.error(error);
        setMetrics((data as Metric[]) ?? []);
        setMetricsLoading(false);
      });

    const channel = supabase
      .channel(`metrics:${user.id}`)
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "campaign_metrics", filter: `user_id=eq.${user.id}` },
        (payload) => {
          setMetrics((prev) => [payload.new as Metric, ...prev]);
        }
      )
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "campaign_metrics", filter: `user_id=eq.${user.id}` },
        (payload) => {
          setMetrics((prev) => prev.map((m) => (m.id === (payload.new as Metric).id ? (payload.new as Metric) : m)));
        }
      )
      .on(
        "postgres_changes",
        { event: "DELETE", schema: "public", table: "campaign_metrics", filter: `user_id=eq.${user.id}` },
        (payload) => {
          setMetrics((prev) => prev.filter((m) => m.id !== (payload.old as Metric).id));
        }
      )
      .subscribe((status) => setLive(status === "SUBSCRIBED"));

    return () => {
      cancelled = true;
      supabase.removeChannel(channel);
    };
  }, [user]);

  const totals = useMemo(() => {
    const t = displayMetrics.reduce(
      (acc, m) => {
        acc.spend += Number(m.spend) || 0;
        acc.reach += m.reach || 0;
        acc.clicks += m.clicks || 0;
        acc.leads += m.leads || 0;
        acc.conversions += m.conversions || 0;
        acc.revenue += Number(m.revenue) || 0;
        return acc;
      },
      { spend: 0, reach: 0, clicks: 0, leads: 0, conversions: 0, revenue: 0 }
    );
    const cpl = t.leads > 0 ? t.spend / t.leads : 0;
    const roas = t.spend > 0 ? t.revenue / t.spend : 0;
    return { ...t, cpl, roas };
  }, [displayMetrics]);

  const series = useMemo(() => {
    // Bucket by day (ascending)
    const buckets = new Map<string, { date: string; spend: number; revenue: number; clicks: number; leads: number; conversions: number; reach: number }>();
    for (const m of displayMetrics) {
      const d = new Date(m.recorded_at);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      const b = buckets.get(key) ?? { date: key, spend: 0, revenue: 0, clicks: 0, leads: 0, conversions: 0, reach: 0 };
      b.spend += Number(m.spend) || 0;
      b.revenue += Number(m.revenue) || 0;
      b.clicks += m.clicks || 0;
      b.leads += m.leads || 0;
      b.conversions += m.conversions || 0;
      b.reach += m.reach || 0;
      buckets.set(key, b);
    }
    return Array.from(buckets.values())
      .sort((a, b) => a.date.localeCompare(b.date))
      .map((b) => ({
        ...b,
        label: new Date(b.date).toLocaleDateString(undefined, { month: "short", day: "numeric" }),
        roas: b.spend > 0 ? Number((b.revenue / b.spend).toFixed(2)) : 0,
        cpl: b.leads > 0 ? Number((b.spend / b.leads).toFixed(2)) : 0,
      }));
  }, [displayMetrics]);

  const handleExportCsv = () => {
    const campaignName = (id: string) => campaigns.find((c) => c.id === id)?.name ?? id;
    const campaignPlatform = (id: string) => campaigns.find((c) => c.id === id)?.platform ?? "";
    const headers = [
      "Date",
      "Platform",
      "Campaign",
      "Impressions",
      "Clicks",
      "CTR (%)",
      "Spend",
      "Revenue",
      "ROAS",
      "Leads",
      "Conversions",
    ];
    // RFC 4180-style escaping: always wrap in quotes for consistent parsing,
    // and double any embedded quotes.
    const escape = (v: string | number) => {
      const s = v === null || v === undefined ? "" : String(v);
      return `"${s.replace(/"/g, '""')}"`;
    };
    // Force YYYY-MM-DD regardless of locale or timezone of the viewer.
    const isoDate = (iso: string) => {
      const d = new Date(iso);
      const y = d.getUTCFullYear();
      const m = String(d.getUTCMonth() + 1).padStart(2, "0");
      const day = String(d.getUTCDate()).padStart(2, "0");
      return `${y}-${m}-${day}`;
    };
    const rows = filteredMetrics.map((m) => {
      const impressions = m.reach || 0;
      const clicks = m.clicks || 0;
      const spend = Number(m.spend) || 0;
      const revenue = Number(m.revenue) || 0;
      const ctr = impressions > 0 ? (clicks / impressions) * 100 : 0;
      const roas = spend > 0 ? revenue / spend : 0;
      return [
        isoDate(m.recorded_at),
        campaignPlatform(m.campaign_id),
        campaignName(m.campaign_id),
        impressions,
        clicks,
        ctr.toFixed(2),
        spend.toFixed(2),
        revenue.toFixed(2),
        roas.toFixed(2),
        m.leads,
        m.conversions,
      ]
        .map(escape)
        .join(",");
    });
    const csv = [headers.map(escape).join(","), ...rows].join("\r\n");
    // BOM helps Excel auto-detect UTF-8 with the proper encoding
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const stamp = new Date().toISOString().slice(0, 10);
    a.href = url;
    a.download = `adkavi-dashboard-${stamp}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (!user) return null;

  if (shareExpired) {
    return <ShareExpiredView />;
  }

  return (
    <div className="min-h-full">
      <section className="max-w-7xl mx-auto px-6 py-10">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight flex items-center gap-3">
              <LayoutDashboard className="w-8 h-8 text-orange-400 shrink-0" />
              <span className="bg-gradient-to-r from-orange-400 to-primary bg-clip-text text-transparent">
                {t("dash.title")}
              </span>
            </h1>
            <p className="mt-2 text-muted-foreground flex items-center gap-3 flex-wrap">
              {activeClient && (
                <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-2.5 py-0.5 text-xs">
                  <span className="w-2 h-2 rounded-full" style={{ background: activeClient.brand_color || "hsl(var(--primary))" }} />
                  <span className="text-foreground font-medium">{activeClient.name}</span>
                </span>
              )}
              <span className="flex items-center gap-1.5">
                {live ? (
                  <><Wifi className="w-3.5 h-3.5 text-primary" /> <span className="text-foreground">{t("dash.live")}</span></>
                ) : (
                  <><WifiOff className="w-3.5 h-3.5" /> {t("dash.connecting")}</>
                )}
              </span>
              {metaLoaded && (
                metaAccount ? (
                  <span
                    className="inline-flex items-center gap-1.5 rounded-full border border-primary/30 bg-primary/10 px-2.5 py-0.5 text-xs text-foreground"
                    title={t("dash.metaConnected").replace("{account}", metaAccount)}
                  >
                    <Link2 className="w-3.5 h-3.5 text-primary" />
                    <span className="font-medium">Meta</span>
                    <span className="text-muted-foreground max-w-[140px] truncate">· {metaAccount}</span>
                  </span>
                ) : (
                  <Button asChild variant="outline" size="sm" className="h-7">
                    <Link to="/settings">
                      <Link2Off className="w-3.5 h-3.5 mr-1.5" /> {t("dash.connectMeta")}
                    </Link>
                  </Button>
                )
              )}
            </p>
          </div>
          <Button asChild>
            <Link to="/campaigns/new"><Sparkles className="w-4 h-4 mr-2" /> {t("dash.newAiCampaign")}</Link>
          </Button>
        </div>
        {hasAnyConnection === false && (
          <div className="mb-6 flex items-center justify-between gap-3 flex-wrap rounded-xl border border-primary/20 bg-primary/5 px-4 py-3">
            <div className="flex items-center gap-2 text-sm">
              <Link2Off className="w-4 h-4 shrink-0 text-muted-foreground" />
              <span>
                <span className="font-medium">{t("dash.connect.prompt")}</span>
                <span className="text-muted-foreground"> {t("dash.connect.desc")}</span>
              </span>
            </div>
            <Button asChild size="sm" variant="secondary">
              <Link to="/settings" search={{ tab: "integrations" }}>{t("dash.connect.btn")}</Link>
            </Button>
          </div>
        )}


        {/* Filters */}
        <div className="mb-8 flex flex-wrap items-center gap-2">
          <Select value={rangePreset} onValueChange={(v) => setRangePreset(v as PresetId)}>
            <SelectTrigger className="w-[160px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              {PRESET_RANGES.map((r) => (
                <SelectItem key={r.id} value={r.id}>{t(r.labelKey)}</SelectItem>
              ))}
              <SelectItem value="custom">{t("dash.range.custom")}</SelectItem>
              <SelectItem value="all">{t("dash.range.all")}</SelectItem>
            </SelectContent>
          </Select>

          <Select
            value={platformFilter}
            onValueChange={(v) => updateSearch({ platform: v as PlatformId | "all" })}
          >
            <SelectTrigger className="w-[160px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              {PLATFORM_OPTIONS.map((p) => (
                <SelectItem key={p.id} value={p.id}>{p.staticLabel ?? t(p.labelKey)}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={campaignId ?? "__all"}
            onValueChange={(v) => updateSearch({ campaign: v === "__all" ? undefined : v })}
          >
            <SelectTrigger className="w-[220px]">
              <SelectValue placeholder={t("dash.allCampaigns")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all">{t("dash.allCampaigns")}</SelectItem>
              {campaigns.map((c) => (
                <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {rangePreset === "custom" && (
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "justify-start text-left font-normal w-[260px]",
                    !fromDate && !toDate && "text-muted-foreground",
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {fromDate && toDate
                    ? `${format(fromDate, "PP")} – ${format(toDate, "PP")}`
                    : fromDate
                      ? `${format(fromDate, "PP")} – …`
                      : toDate
                        ? `… – ${format(toDate, "PP")}`
                        : t("dash.pickDateRange")}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="range"
                  numberOfMonths={2}
                  selected={{ from: fromDate, to: toDate } as DateRange}
                  onSelect={(r: DateRange | undefined) =>
                    updateSearch({
                      from: r?.from ? r.from.toISOString() : undefined,
                      to: r?.to ? r.to.toISOString() : undefined,
                    })
                  }
                  defaultMonth={fromDate ?? new Date()}
                  initialFocus
                  className={cn("p-3 pointer-events-auto")}
                />
              </PopoverContent>
            </Popover>
          )}

          <Select
            value={currency}
            onValueChange={(v) => setCurrency(v as Currency)}
          >
            <SelectTrigger className="w-[110px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {CURRENCIES.map((c) => (
                <SelectItem key={c} value={c}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrency(detectCurrencyFromLocale())}
            title={t("dash.useBrowserCurrencyTitle")}
          >
            <Globe className="w-4 h-4 mr-1" /> {t("dash.useBrowserCurrency")}
          </Button>

          {hasFilters && (
            <Button variant="ghost" size="sm" onClick={clearFilters}>
              <X className="w-4 h-4 mr-1" /> {t("dash.clear")}
            </Button>
          )}

          <Button
            variant="outline"
            size="sm"
            onClick={handleExportCsv}
            disabled={metricsLoading || filteredMetrics.length === 0}
            className="ml-auto"
            title={t("dash.exportCsvTitle")}
          >
            <Download className="w-4 h-4 mr-1" /> {t("dash.exportCsv")}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={async () => {
              const params = new URLSearchParams();
              if (search.range) params.set("range", search.range);
              if (search.platform) params.set("platform", search.platform);
              if (search.from) params.set("from", search.from);
              if (search.to) params.set("to", search.to);
              if (search.campaign) params.set("campaign", search.campaign);
              if (search.currency) params.set("currency", search.currency);
              const expiresAt = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);
              params.set("expires", String(Math.floor(expiresAt.getTime() / 1000)));
              const url = `${window.location.origin}/dashboard?${params.toString()}`;
              const expiresLabel = expiresAt.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
              try {
                await navigator.clipboard.writeText(url);
                toast.success(t("dash.shareCopied"), {
                  description: t("dash.shareExpires").replace("{date}", expiresLabel),
                });
              } catch {
                toast.message(t("dash.shareFallback"), { description: `${url}\n${t("dash.shareExpires").replace("{date}", expiresLabel)}` });
              }
            }}
            title={t("dash.shareTitle")}
          >
            <Share2 className="w-4 h-4 mr-1" /> {t("dash.share")}
          </Button>
        </div>

        <DashboardSectionBoundary label="campaign-health">
          <CampaignHealthWidget />
        </DashboardSectionBoundary>

        <DashboardSectionBoundary label="ai-agent">
          <AiAgentDashboardCard />
        </DashboardSectionBoundary>

        <DashboardSectionBoundary label="platform-breakdown">
          <PlatformBreakdown formatCurrency={formatCurrency} platformFilter={platformFilter} />
        </DashboardSectionBoundary>

        <DashboardSectionBoundary label="audience-insights">
          <AudienceInsights seed={user?.id ?? "default"} />
        </DashboardSectionBoundary>

        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <MetricCard icon={DollarSign} label={t("dash.metric.spend")} value={formatCurrency(totals.spend)} loading={metricsLoading} onClick={() => { setDrillMetric("spend"); setDrillLabel(t("dash.metric.spend")); setDrillValue(formatCurrency(totals.spend)); }} />
          <MetricCard icon={Eye} label={t("dash.metric.reach")} value={formatNumber(totals.reach)} loading={metricsLoading} onClick={() => { setDrillMetric("reach"); setDrillLabel(t("dash.metric.reach")); setDrillValue(formatNumber(totals.reach)); }} />
          <MetricCard icon={MousePointerClick} label={t("dash.metric.clicks")} value={formatNumber(totals.clicks)} loading={metricsLoading} onClick={() => { setDrillMetric("clicks"); setDrillLabel(t("dash.metric.clicks")); setDrillValue(formatNumber(totals.clicks)); }} />
          <MetricCard icon={UserPlus} label={t("dash.metric.leads")} value={formatNumber(totals.leads)} loading={metricsLoading} onClick={() => { setDrillMetric("leads"); setDrillLabel(t("dash.metric.leads")); setDrillValue(formatNumber(totals.leads)); }} />
          <MetricCard icon={Target} label={t("dash.metric.conversions")} value={formatNumber(totals.conversions)} loading={metricsLoading} onClick={() => { setDrillMetric("conversions"); setDrillLabel(t("dash.metric.conversions")); setDrillValue(formatNumber(totals.conversions)); }} />
          <MetricCard icon={DollarSign} label={t("dash.metric.cpl")} value={totals.leads > 0 ? formatCurrency(totals.cpl) : "—"} loading={metricsLoading} accent onClick={() => { setDrillMetric("cpl"); setDrillLabel(t("dash.metric.cpl")); setDrillValue(totals.leads > 0 ? formatCurrency(totals.cpl) : "—"); }} />
          <MetricCard icon={TrendingUp} label={t("dash.metric.roas")} value={totals.spend > 0 ? `${totals.roas.toFixed(2)}×` : "—"} loading={metricsLoading} accent onClick={() => { setDrillMetric("roas"); setDrillLabel(t("dash.metric.roas")); setDrillValue(totals.spend > 0 ? `${totals.roas.toFixed(2)}×` : "—"); }} />
          <MetricCard icon={DollarSign} label={t("dash.metric.revenue")} value={formatCurrency(totals.revenue)} loading={metricsLoading} onClick={() => { setDrillMetric("revenue"); setDrillLabel(t("dash.metric.revenue")); setDrillValue(formatCurrency(totals.revenue)); }} />
        </div>

        <DashboardSectionBoundary label="analytics-insights">
          <AnalyticsInsights formatCurrency={formatCurrency} />
        </DashboardSectionBoundary>

        <MetricDetailDialog
          open={drillMetric !== null}
          onOpenChange={(o) => { if (!o) setDrillMetric(null); }}
          metric={drillMetric}
          label={drillLabel}
          value={drillValue}
          series={series}
          formatCurrency={formatCurrency}
        />

        {metricsLoading && (
          <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-4">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="rounded-2xl border border-border/50 bg-card/40 backdrop-blur p-5">
                <div className="mb-3 flex items-baseline justify-between">
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-3 w-20" />
                </div>
                <Skeleton className="h-56 w-full" />
              </div>
            ))}
          </div>
        )}

        {!metricsLoading && displayMetrics.length === 0 && (
          <div className="mt-10 rounded-2xl border border-dashed border-border/60 bg-card/30 p-10 text-center">
            <Activity className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
            <h3 className="text-lg font-semibold">{t("dash.empty.title")}</h3>
            <p className="mt-1 text-sm text-muted-foreground max-w-md mx-auto">
              {t("dash.empty.desc")}
            </p>
          </div>
        )}

        {displayMetrics.length > 0 && series.length > 0 && (
          <DashboardSectionBoundary label="charts-grid">
            <ChartsGrid series={series} formatCurrency={formatCurrency} />
          </DashboardSectionBoundary>
        )}

        {displayMetrics.length > 0 && (
          <div className="mt-10 rounded-2xl border border-border/50 bg-card/40 backdrop-blur overflow-hidden">
            <div className="px-6 py-4 border-b border-border/50 flex items-center justify-between">
              <h2 className="font-semibold flex items-center gap-2">
                {t("dash.recent")}
              </h2>
              <span className="text-xs text-muted-foreground">{t("dash.recentLast").replace("{n}", String(Math.min(displayMetrics.length, 20)))}</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[720px]">
                <thead className="text-xs uppercase text-muted-foreground bg-background/30">
                  <tr>
                    <th className="text-left px-4 sm:px-6 py-3 font-medium">{t("dash.col.when")}</th>
                    <th className="text-right px-3 sm:px-4 py-3 font-medium">{t("dash.col.spend")}</th>
                    <th className="text-right px-3 sm:px-4 py-3 font-medium">{t("dash.col.reach")}</th>
                    <th className="text-right px-3 sm:px-4 py-3 font-medium">{t("dash.col.clicks")}</th>
                    <th className="text-right px-3 sm:px-4 py-3 font-medium">{t("dash.col.leads")}</th>
                    <th className="text-right px-3 sm:px-4 py-3 font-medium">{t("dash.col.conv")}</th>
                    <th className="text-right px-4 sm:px-6 py-3 font-medium">{t("dash.col.revenue")}</th>
                  </tr>
                </thead>
                <tbody>
                  {displayMetrics.slice(0, 20).map((m) => (
                    <tr key={m.id} className="border-t border-border/30 hover:bg-background/20">
                      <td className="px-4 sm:px-6 py-4 sm:py-3 text-muted-foreground whitespace-nowrap">{format(new Date(m.recorded_at), "MMM d")}</td>
                      <td className="px-3 sm:px-4 py-4 sm:py-3 text-right">{formatCurrency(Number(m.spend))}</td>
                      <td className="px-3 sm:px-4 py-4 sm:py-3 text-right">{formatNumber(m.reach)}</td>
                      <td className="px-3 sm:px-4 py-4 sm:py-3 text-right">{formatNumber(m.clicks)}</td>
                      <td className="px-3 sm:px-4 py-4 sm:py-3 text-right">{formatNumber(m.leads)}</td>
                      <td className="px-3 sm:px-4 py-4 sm:py-3 text-right">{formatNumber(m.conversions)}</td>
                      <td className="px-4 sm:px-6 py-4 sm:py-3 text-right">{formatCurrency(Number(m.revenue))}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}

function MetricCard({
  icon: Icon,
  label,
  value,
  loading,
  accent = false,
  onClick,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string;
  loading: boolean;
  accent?: boolean;
  onClick?: () => void;
}) {
  const { t } = useI18n();
  const interactive = !!onClick && !loading;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!interactive}
      aria-label={interactive ? `${label} — ${t("metric.detail.openAria")}` : label}
      className={`text-left w-full rounded-2xl border ${accent ? "border-primary/40 bg-primary/5" : "border-border/50 bg-card/40"} backdrop-blur p-5 transition ${interactive ? "hover:border-primary/60 hover:bg-primary/10 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary" : "cursor-default"}`}
    >
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-wider text-muted-foreground">{label}</span>
        <Icon className={`w-4 h-4 ${accent ? "text-primary" : "text-muted-foreground"}`} />
      </div>
      <div className="mt-3 text-2xl font-bold tabular-nums">
        {loading ? <span className="inline-block h-7 w-24 rounded bg-muted/40 animate-pulse" /> : value}
      </div>
    </button>
  );
}


function formatNumber(n: number) {
  return new Intl.NumberFormat("en-US").format(n);
}

type SeriesPoint = {
  date: string;
  label: string;
  spend: number;
  revenue: number;
  clicks: number;
  leads: number;
  conversions: number;
  reach: number;
  roas: number;
  cpl: number;
};

function ChartCard({
  title,
  subtitle,
  children,
  chartRef,
  fileName,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  chartRef: RefObject<HTMLDivElement | null>;
  fileName: string;
}) {
  const { t } = useI18n();
  const handleDownload = async (format: "png" | "svg") => {
    const node = chartRef.current;
    if (!node) return;
    try {
      const bg = getComputedStyle(document.body).backgroundColor || "#0b0b0c";
      const dataUrl = format === "svg"
        ? await toSvg(node, { cacheBust: true, backgroundColor: bg })
        : await toPng(node, { cacheBust: true, pixelRatio: 2, backgroundColor: bg });
      const a = document.createElement("a");
      const stamp = new Date().toISOString().slice(0, 10);
      a.href = dataUrl;
      a.download = `${fileName}-${stamp}.${format}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (err) {
      toast.error(t("dash.chart.exportFailed"), { description: (err as Error).message });
    }
  };

  return (
    <div ref={chartRef} className="rounded-2xl border border-border/50 bg-card/40 backdrop-blur p-5 overflow-hidden">
      <div className="mb-3 flex items-baseline justify-between gap-2 flex-wrap">
        <h3 className="font-semibold">{title}</h3>
        <div className="flex items-center gap-2">
          {subtitle && <span className="text-xs text-muted-foreground">{subtitle}</span>}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                title={t("dash.chart.download")}
                data-html2canvas-ignore="true"
              >
                <ImageDown className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => handleDownload("png")}>{t("dash.chart.downloadPng")}</DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleDownload("svg")}>{t("dash.chart.downloadSvg")}</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      {children}
    </div>
  );
}

function RichTooltip({
  active,
  payload,
  formatCurrency,
}: {
  active?: boolean;
  payload?: Array<{ payload: SeriesPoint }>;
  formatCurrency: (n: number) => string;
}) {
  const { t } = useI18n();
  if (!active || !payload || payload.length === 0) return null;
  const p = payload[0].payload;
  const ctr = p.reach > 0 ? (p.clicks / p.reach) * 100 : 0;
  const convRate = p.clicks > 0 ? (p.conversions / p.clicks) * 100 : 0;
  const cpc = p.clicks > 0 ? p.spend / p.clicks : 0;
  return (
    <div className="rounded-lg border border-border/60 bg-background/95 backdrop-blur px-3 py-2 text-xs shadow-md min-w-[200px]">
      <div className="font-semibold mb-1.5 text-foreground">
        {new Date(p.date).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", year: "numeric" })}
      </div>
      <div className="grid grid-cols-2 gap-x-4 gap-y-1 tabular-nums">
        <span className="text-muted-foreground">{t("dash.metric.spend")}</span>
        <span className="text-right font-medium">{formatCurrency(p.spend)}</span>
        <span className="text-muted-foreground">{t("dash.metric.revenue")}</span>
        <span className="text-right font-medium">{formatCurrency(p.revenue)}</span>
        <span className="text-muted-foreground">{t("dash.metric.roas")}</span>
        <span className="text-right font-medium">{p.roas.toFixed(2)}×</span>
        <span className="text-muted-foreground">{t("dash.metric.impressions")}</span>
        <span className="text-right font-medium">{formatNumber(p.reach)}</span>
        <span className="text-muted-foreground">{t("dash.metric.clicks")}</span>
        <span className="text-right font-medium">{formatNumber(p.clicks)}</span>
        <span className="text-muted-foreground">{t("dash.metric.ctr")}</span>
        <span className="text-right font-medium">{ctr.toFixed(2)}%</span>
        <span className="text-muted-foreground">{t("dash.metric.cpc")}</span>
        <span className="text-right font-medium">{formatCurrency(cpc)}</span>
        <span className="text-muted-foreground">{t("dash.metric.leads")}</span>
        <span className="text-right font-medium">{formatNumber(p.leads)}</span>
        <span className="text-muted-foreground">{t("dash.metric.conversions")}</span>
        <span className="text-right font-medium">{formatNumber(p.conversions)}</span>
        <span className="text-muted-foreground">{t("dash.metric.convRate")}</span>
        <span className="text-right font-medium">{convRate.toFixed(2)}%</span>
      </div>
    </div>
  );
}

function ChartsGrid({
  series,
  formatCurrency,
}: {
  series: SeriesPoint[];
  formatCurrency: (n: number) => string;
}) {
  const { t } = useI18n();
  const spendRef = useRef<HTMLDivElement | null>(null);
  const funnelRef = useRef<HTMLDivElement | null>(null);
  const roasRef = useRef<HTMLDivElement | null>(null);
  const reachRef = useRef<HTMLDivElement | null>(null);

  const tooltip = <ChartTooltip content={<RichTooltip formatCurrency={formatCurrency} />} />;

  return (
    <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-4">
      <ChartCard title={t("dash.chart.spendRevenue")} subtitle={t("dash.chart.spendRevenueSub")} chartRef={spendRef} fileName="spend-revenue">
        <ChartContainer
          config={{
            spend: { label: "Spend", color: "hsl(var(--muted-foreground))" },
            revenue: { label: "Revenue", color: "hsl(var(--primary))" },
          } satisfies ChartConfig}
          className="aspect-auto h-64 w-full"
        >
          <AreaChart data={series} margin={{ left: 4, right: 8, top: 8, bottom: 0 }}>
            <defs>
              <linearGradient id="fillRevenue" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="var(--color-revenue)" stopOpacity={0.5} />
                <stop offset="95%" stopColor="var(--color-revenue)" stopOpacity={0.05} />
              </linearGradient>
              <linearGradient id="fillSpend" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="var(--color-spend)" stopOpacity={0.4} />
                <stop offset="95%" stopColor="var(--color-spend)" stopOpacity={0.05} />
              </linearGradient>
            </defs>
            <CartesianGrid vertical={false} strokeDasharray="3 3" />
            <XAxis dataKey="label" tickLine={false} axisLine={false} tickMargin={8} minTickGap={24} />
            <YAxis tickLine={false} axisLine={false} tickMargin={8} width={48} />
            {tooltip}
            <Area type="monotone" dataKey="spend" stroke="var(--color-spend)" fill="url(#fillSpend)" strokeWidth={2} />
            <Area type="monotone" dataKey="revenue" stroke="var(--color-revenue)" fill="url(#fillRevenue)" strokeWidth={2} />
          </AreaChart>
        </ChartContainer>
      </ChartCard>

      <ChartCard title={t("dash.chart.funnel")} subtitle={t("dash.chart.funnelSub")} chartRef={funnelRef} fileName="funnel">
        <ChartContainer
          config={{
            clicks: { label: "Clicks", color: "hsl(var(--muted-foreground))" },
            leads: { label: "Leads", color: "hsl(var(--primary))" },
            conversions: { label: "Conversions", color: "hsl(var(--secondary))" },
          } satisfies ChartConfig}
          className="aspect-auto h-64 w-full"
        >
          <LineChart data={series} margin={{ left: 4, right: 8, top: 8, bottom: 0 }}>
            <CartesianGrid vertical={false} strokeDasharray="3 3" />
            <XAxis dataKey="label" tickLine={false} axisLine={false} tickMargin={8} minTickGap={24} />
            <YAxis tickLine={false} axisLine={false} tickMargin={8} width={36} />
            {tooltip}
            <Line type="monotone" dataKey="clicks" stroke="var(--color-clicks)" strokeWidth={2} dot={false} />
            <Line type="monotone" dataKey="leads" stroke="var(--color-leads)" strokeWidth={2} dot={false} />
            <Line type="monotone" dataKey="conversions" stroke="var(--color-conversions)" strokeWidth={2} dot={false} />
          </LineChart>
        </ChartContainer>
      </ChartCard>

      <ChartCard title={t("dash.chart.roas")} subtitle={t("dash.chart.roasSub")} chartRef={roasRef} fileName="roas">
        <ChartContainer
          config={{ roas: { label: "ROAS", color: "hsl(var(--primary))" } } satisfies ChartConfig}
          className="aspect-auto h-56 w-full"
        >
          <AreaChart data={series} margin={{ left: 4, right: 8, top: 8, bottom: 0 }}>
            <defs>
              <linearGradient id="fillRoas" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="var(--color-roas)" stopOpacity={0.5} />
                <stop offset="95%" stopColor="var(--color-roas)" stopOpacity={0.05} />
              </linearGradient>
            </defs>
            <CartesianGrid vertical={false} strokeDasharray="3 3" />
            <XAxis dataKey="label" tickLine={false} axisLine={false} tickMargin={8} minTickGap={24} />
            <YAxis tickLine={false} axisLine={false} tickMargin={8} width={36} />
            {tooltip}
            <Area type="monotone" dataKey="roas" stroke="var(--color-roas)" fill="url(#fillRoas)" strokeWidth={2} />
          </AreaChart>
        </ChartContainer>
      </ChartCard>

      <ChartCard title={t("dash.chart.reach")} subtitle={t("dash.chart.reachSub")} chartRef={reachRef} fileName="reach">
        <ChartContainer
          config={{ reach: { label: "Reach", color: "hsl(var(--secondary))" } } satisfies ChartConfig}
          className="aspect-auto h-56 w-full"
        >
          <AreaChart data={series} margin={{ left: 4, right: 8, top: 8, bottom: 0 }}>
            <defs>
              <linearGradient id="fillReach" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="var(--color-reach)" stopOpacity={0.5} />
                <stop offset="95%" stopColor="var(--color-reach)" stopOpacity={0.05} />
              </linearGradient>
            </defs>
            <CartesianGrid vertical={false} strokeDasharray="3 3" />
            <XAxis dataKey="label" tickLine={false} axisLine={false} tickMargin={8} minTickGap={24} />
            <YAxis tickLine={false} axisLine={false} tickMargin={8} width={48} />
            {tooltip}
            <Area type="monotone" dataKey="reach" stroke="var(--color-reach)" fill="url(#fillReach)" strokeWidth={2} />
          </AreaChart>
        </ChartContainer>
      </ChartCard>
    </div>
  );
}


function sanitizeOriginalPath(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const s = String(raw).trim();
  if (!s || s.length > 500) return null;
  if (s.startsWith("//") || s.startsWith("\\\\")) return null;
  if (/^[a-z][a-z0-9+.-]*:/i.test(s)) return null;
  if (!s.startsWith("/")) return null;
  if (/[\u0000-\u001f\u007f]/.test(s)) return null;
  return s;
}

function formatCountdown(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const pad = (n: number) => n.toString().padStart(2, "0");
  return `${pad(h)}:${pad(m)}:${pad(sec)}`;
}

function ShareExpiredView() {
  const { t } = useI18n();
  const [email, setEmail] = useState("");
  const [confirmEmail, setConfirmEmail] = useState("");
  const [note, setNote] = useState("");
  const [company, setCompany] = useState(""); // honeypot
  const [captchaAnswer, setCaptchaAnswer] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState<string | null>(null);
  const [retrySeconds, setRetrySeconds] = useState<number | null>(null);

  // Stable captcha (regenerates if user reloads). Using small numbers for accessibility.
  const captcha = useMemo(() => {
    const a = Math.floor(Math.random() * 8) + 2; // 2..9
    const b = Math.floor(Math.random() * 8) + 2;
    return { question: `${a} + ${b}`, a, b };
  }, []);

  // Live countdown when rate-limited; auto-resets the form when it hits zero.
  useEffect(() => {
    if (retrySeconds === null) return;
    if (retrySeconds <= 0) {
      setRetrySeconds(null);
      setCaptchaAnswer("");
      toast.success(t("share.request.retryReady"));
      return;
    }
    const id = window.setInterval(() => {
      setRetrySeconds((cur) => (cur === null ? null : cur - 1));
    }, 1000);
    return () => window.clearInterval(id);
  }, [retrySeconds, t]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (retrySeconds !== null && retrySeconds > 0) return;

    const trimmed = email.trim();
    const trimmedConfirm = confirmEmail.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      toast.error(t("share.request.invalidEmail"));
      return;
    }
    if (trimmed.toLowerCase() !== trimmedConfirm.toLowerCase()) {
      toast.error(t("share.request.emailMismatch"));
      return;
    }
    if (Number(captchaAnswer) !== captcha.a + captcha.b) {
      toast.error(t("share.request.captchaError"));
      return;
    }

    setSubmitting(true);
    try {
      const originalPath = sanitizeOriginalPath(
        typeof window !== "undefined" ? window.location.pathname + window.location.search : null
      );

      const result = await requestShareLink({
        data: {
          email: trimmed,
          confirmEmail: trimmedConfirm,
          message: note.trim() || null,
          originalPath,
          company,
          captchaQuestion: captcha.question,
          captchaAnswer: captchaAnswer.trim(),
        },
      });

      if (result.ok) {
        setSubmittedEmail(result.email);
        toast.success(t("share.request.success"));
        return;
      }

      switch (result.reason) {
        case "rate_limited":
        case "ip_rate_limited":
          if (typeof result.retryAfterSeconds === "number") {
            setRetrySeconds(result.retryAfterSeconds);
          }
          toast.error(t("share.request.rateLimited"));
          break;
        case "duplicate":
          toast.info(t("share.request.duplicate"));
          setSubmittedEmail(trimmed);
          break;
        case "invalid_email":
          toast.error(t("share.request.invalidEmail"));
          break;
        case "email_mismatch":
          toast.error(t("share.request.emailMismatch"));
          break;
        case "captcha":
          toast.error(t("share.request.captchaError"));
          setCaptchaAnswer("");
          break;
        case "honeypot":
          toast.error(t("share.request.botBlocked"));
          break;
        default:
          toast.error(t("share.request.error"));
      }
    } catch {
      toast.error(t("share.request.error"));
    } finally {
      setSubmitting(false);
    }
  };

  if (submittedEmail) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center px-6 py-16">
        <div
          role="status"
          aria-live="polite"
          className="max-w-md w-full rounded-2xl border border-emerald-500/30 bg-card/60 backdrop-blur p-8 text-center shadow-lg"
        >
          <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-emerald-500/15 ring-4 ring-emerald-500/10">
            <svg className="h-8 w-8 text-emerald-500" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold tracking-tight">{t("share.request.successTitle")}</h1>
          <p className="mt-3 text-sm text-muted-foreground">
            {t("share.request.successBody")}{" "}
            <span className="font-medium text-foreground break-all">{submittedEmail}</span>.
          </p>
          <p className="mt-3 text-xs text-muted-foreground">
            {t("share.request.successFootnote")}
          </p>
          <Button asChild className="mt-6 w-full">
            <Link to="/">{t("share.request.goHome")}</Link>
          </Button>
        </div>
      </div>
    );
  }

  const rateLimited = retrySeconds !== null && retrySeconds > 0;

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-6 py-16">
      <div className="max-w-md w-full rounded-2xl border border-border/60 bg-card/60 backdrop-blur p-8">
        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-amber-500/10">
          <Clock className="h-6 w-6 text-amber-500" />
        </div>
        <h1 className="text-xl font-bold tracking-tight text-center">{t("share.expired.title")}</h1>
        <p className="mt-2 text-sm text-muted-foreground text-center">{t("share.expired.body")}</p>

        {rateLimited && (
          <div
            role="status"
            aria-live="polite"
            aria-atomic="true"
            className="mt-4 rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-center"
          >
            <p className="text-xs text-muted-foreground">{t("share.request.rateLimitedCountdown")}</p>
            <p className="mt-1 font-mono text-lg font-semibold tabular-nums text-destructive">
              <span aria-hidden="true">{formatCountdown(retrySeconds!)}</span>
              <span className="sr-only">{Math.ceil(retrySeconds! / 60)} minutes remaining</span>
            </p>
          </div>
        )}

        <form onSubmit={submit} className="mt-6 space-y-3" noValidate aria-label={t("share.request.formAriaLabel")}>
          {/* Honeypot — hidden from users, visible to naive bots */}
          <div aria-hidden="true" className="absolute left-[-9999px] top-auto h-0 w-0 overflow-hidden">
            <label htmlFor="req-company">Company</label>
            <input
              id="req-company"
              type="text"
              tabIndex={-1}
              autoComplete="off"
              value={company}
              onChange={(e) => setCompany(e.target.value)}
            />
          </div>

          <div>
            <label htmlFor="req-email" className="text-xs font-medium text-muted-foreground">
              {t("share.request.emailLabel")}
            </label>
            <input
              id="req-email"
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t("share.request.emailPlaceholder")}
              className="mt-1 flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            />
          </div>
          <div>
            <label htmlFor="req-email-confirm" className="text-xs font-medium text-muted-foreground">
              {t("share.request.confirmEmailLabel")}
            </label>
            <input
              id="req-email-confirm"
              type="email"
              required
              autoComplete="off"
              value={confirmEmail}
              onChange={(e) => setConfirmEmail(e.target.value)}
              onPaste={(e) => e.preventDefault()}
              placeholder={t("share.request.confirmEmailPlaceholder")}
              className="mt-1 flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            />
            {confirmEmail.length > 0 && email.trim().toLowerCase() !== confirmEmail.trim().toLowerCase() && (
              <p className="mt-1 text-xs text-destructive">{t("share.request.emailMismatch")}</p>
            )}
          </div>
          <div>
            <label htmlFor="req-note" className="text-xs font-medium text-muted-foreground">
              {t("share.request.note")}
            </label>
            <textarea
              id="req-note"
              rows={3}
              maxLength={1000}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={t("share.request.notePlaceholder")}
              className="mt-1 flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            />
          </div>
          <div>
            <label htmlFor="req-captcha" className="text-xs font-medium text-muted-foreground">
              <span aria-hidden="true">
                {t("share.request.captchaLabel", { q: captcha.question })}
              </span>
              <span className="sr-only">
                {t("share.request.captchaLabel", { q: `${captcha.a} plus ${captcha.b}` })}
              </span>
            </label>
            <input
              id="req-captcha"
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              required
              autoComplete="off"
              aria-required="true"
              aria-describedby="req-captcha-help"
              value={captchaAnswer}
              onChange={(e) => setCaptchaAnswer(e.target.value)}
              placeholder={t("share.request.captchaPlaceholder")}
              className="mt-1 flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            />
            <p id="req-captcha-help" className="sr-only">
              {t("share.request.captchaHelp")}
            </p>
          </div>
          <Button type="submit" className="w-full" disabled={submitting || rateLimited}>
            {submitting
              ? t("share.request.sending")
              : rateLimited
                ? formatCountdown(retrySeconds!)
                : t("share.request.submit")}
          </Button>
        </form>

        <div className="mt-6 flex items-center justify-center">
          <Button asChild variant="ghost" size="sm">
            <Link to="/">{t("share.expired.goHome")}</Link>
          </Button>
        </div>
      </div>
    </div>
  );
}

function AiAgentDashboardCard() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [enabled, setEnabled] = React.useState(false);
  const [convoCount, setConvoCount] = React.useState(0);
  const [lastReply, setLastReply] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    if (!user) return;
    const stored = localStorage.getItem(`adkavi.aiAgent.agent.${user.id}`);
    let agentEnabled = false;
    if (stored) {
      try { agentEnabled = JSON.parse(stored).enabled === true; } catch { /* noop */ }
    }
    setEnabled(agentEnabled);

    if (!agentEnabled) { setLoading(false); return; }

    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);

    supabase
      .from("conversations")
      .select("id, created_at", { count: "exact" })
      .eq("user_id", user.id)
      .gte("created_at", todayStart.toISOString())
      .order("created_at", { ascending: false })
      .limit(1)
      .then(({ data, count }) => {
        setConvoCount(count ?? 0);
        if (data && data.length > 0) {
          setLastReply(data[0].created_at);
        }
        setLoading(false);
      });
  }, [user]);

  function timeAgo(iso: string, tFn: (k: string, v?: Record<string, string>) => string): string {
    const diffMs = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diffMs / 60000);
    if (mins < 1) return tFn("dash.agent.justNow");
    if (mins < 60) return tFn("dash.agent.minsAgo", { n: String(mins) });
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return tFn("dash.agent.hoursAgo", { n: String(hrs) });
    return tFn("dash.agent.daysAgo", { n: String(Math.floor(hrs / 24)) });
  }

  if (loading) return null;

  if (!enabled) {
    return (
      <div className="mt-6 rounded-2xl border border-border/50 bg-card/40 backdrop-blur p-6 flex flex-col sm:flex-row items-start sm:items-center gap-5">
        <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-orange-500/10 flex items-center justify-center">
          <Bot className="w-6 h-6 text-orange-400" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-base">{t("dash.agent.setupTitle")}</h3>
          <p className="mt-1 text-sm text-muted-foreground max-w-xl">
            {t("dash.agent.setupDesc")}
          </p>
        </div>
        <Button
          asChild
          className="shrink-0 bg-orange-500 hover:bg-orange-600 text-white rounded-full px-5"
        >
          <Link to="/settings" search={{ tab: "ai-agent" }}>
            {t("dash.agent.setupCta")} <ArrowRight className="w-4 h-4 ml-1.5" />
          </Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="mt-6 rounded-2xl border border-primary/30 bg-primary/5 backdrop-blur p-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-orange-500/10 flex items-center justify-center shrink-0">
            <Bot className="w-5 h-5 text-orange-400" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="font-semibold text-sm">{t("dash.agent.name")}</span>
              <span className="inline-flex items-center gap-1 text-xs text-emerald-500 font-medium">
                <CheckCircle2 className="w-3.5 h-3.5" /> {t("dash.agent.active")}
              </span>
            </div>
            <p className="text-xs text-muted-foreground mt-0.5">{t("dash.agent.monitoring")}</p>
          </div>
        </div>
        <Button
          asChild
          variant="outline"
          size="sm"
          className="rounded-full text-xs"
        >
          <Link to="/inbox">
            <MessageSquare className="w-3.5 h-3.5 mr-1.5" /> {t("dash.agent.viewConvos")}
          </Link>
        </Button>
      </div>
      <div className="mt-5 grid grid-cols-2 gap-3">
        <div className="rounded-xl bg-card/60 border border-border/40 p-4">
          <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1">
            <MessageSquare className="w-3.5 h-3.5" /> {t("dash.agent.convosToday")}
          </div>
          <div className="text-2xl font-bold tabular-nums">{convoCount}</div>
        </div>
        <div className="rounded-xl bg-card/60 border border-border/40 p-4">
          <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1">
            <ClockIcon className="w-3.5 h-3.5" /> {t("dash.agent.lastReply")}
          </div>
          <div className="text-2xl font-bold tabular-nums">
            {lastReply ? timeAgo(lastReply, t) : "—"}
          </div>
        </div>
      </div>
    </div>
  );
}

